import { useAuth } from '@/Services/AuthContext';
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SplashScreen from './SplashScreen';
import ConfirmationDialog from '@/Elements/ConfirmationDialog';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import UseDarkMode from '@/Elements/UseDarkMode';
import { Button } from '@/components/ui/button';

import { ChevronDown, Moon, Sun, Star, Award, Crown, TrendingUp, ExternalLink, StarIcon, TicketCheck, CircleStar, AlarmClockCheck, CalendarRange, CircleGauge } from 'lucide-react'; // Added Star, Award, Crown for tier icons
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Card, CardDescription, CardHeader, CardTitle, CardFooter, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

import {
    Label,
    PolarGrid,
    PolarRadiusAxis,
    RadialBar,
    RadialBarChart,
} from "recharts"
import { ChartContainer } from "@/components/ui/chart"
import Navbar from '@/Elements/Navbar';
import { useNavigate } from 'react-router-dom';
import MainContainer from '@/Elements/MainContainer';


const HomeScreen = () => {
    const { user, isAuthenticated, loading, login, logout } = useAuth();
    const [isDarkMode, toggleDarkMode] = UseDarkMode();
    const [showLogoutDialog, setShowLogoutDialog] = useState(false);

    
    const [userTierData, setUserTierData] = useState({
        tier: 'S3',
        points: 19000,
        nextTierPoints: 1100,
        nextTierLevel: 'S4',
        isUpgradeAvailable: true,
    });
   

    // Component for displaying a user's membership tier card
    const MembershipCard = ({ userName, tier, points, nextTierPoints, nextTierLevel, gradientColors, icon: Icon, isUpgradeAvailable, onUpgradeClick }) => {
        // Calculate progress for the progress bar
        const progress = nextTierPoints ? ((points / (points + nextTierPoints)) * 100) : 100;

        return (
            <div className={`h-40 w-full ${gradientColors} flex justify-between items-center mt-5 sm:mt-10 rounded-lg shadow rounded-tr-3xl p-4 relative overflow-hidden`}>
                {/* Subtle background elements for visual flair, mimicking the design's sparkles */}
                <div className="absolute -top-8 -right-8 w-32 h-32 bg-white/10 rounded-full blur-xl"></div>
                <div className="absolute -bottom-8 -left-8 w-24 h-24 bg-white/10 rounded-full blur-xl"></div>

                <div className="flex flex-col justify-between h-full z-10">
                    <h4 className="text-white text-lg font-bold">{userName}</h4>
                    <div className="flex items-center text-white text-sm opacity-90">
                        <span className="mr-1">Points: {points.toLocaleString()}</span> • <span className="ml-1">{tier}</span>
                    </div>
                    {nextTierPoints !== null && ( // Only show progress if there's a next tier
                        <div className="w-full mt-2">
                            <p className="text-white text-xs mb-1">Get {nextTierPoints.toLocaleString()} more points to go {nextTierLevel}</p>
                            <div className="w-full bg-white/30 rounded-full h-1.5">
                                <div className="bg-white h-1.5 rounded-full" style={{ width: `${progress}%` }}></div>
                            </div>
                            <div className="flex justify-between text-white text-xs mt-1">
                                <span>{tier}</span>
                                <span>{nextTierLevel}</span>
                            </div>
                        </div>
                    )}
                </div>

                <div className="flex flex-col items-end justify-between h-full z-10">
                    {Icon && <Icon size={80} className="text-white drop-shadow-lg" />} {/* Render the tier icon */}
                    {isUpgradeAvailable && (
                        <Button onClick={onUpgradeClick} className="bg-white text-gray-800 hover:bg-gray-100 px-4 py-2 rounded-full text-sm font-semibold">
                            Upgrade
                        </Button>
                    )}
                </div>
            </div>
        );
    };

    // Component for when the user has no membership
    const NoMembershipCard = ({ onGetAccessClick }) => {
        return (
            <div className='h-40 w-full bg-gray-800 flex flex-col justify-center items-center mt-5 sm:mt-10 rounded-lg shadow rounded-tr-3xl p-4 text-white'>
                <h4 className="text-lg font-bold mb-2">No Membership Yet!</h4>
                <p className="text-sm text-center mb-4 opacity-80">Unlock exclusive benefits and rewards.</p>
                <Button onClick={onGetAccessClick} className="bg-primary text-white hover:bg-primary/90 px-6 py-2 rounded-full font-semibold">
                    Get Access Pass
                </Button>
            </div>
        );
    };

    const BookingBlock = ({ }) => {
        const navigate = useNavigate();
        return (
            <button className="grow" onClick={() => { navigate('/booking') }}>
                <Card className="w-full text-left border-0 group hover:shadow-lg m-0 transition-all relative">
                    <ExternalLink className='absolute top-5 right-5 group-hover:text-primary'></ExternalLink>
                    <CardHeader className=''>
                        <CardDescription className="tracking-wide transition-all flex items-center gap-1 group-hover:border-accent group-hover:border-l-4 group-hover:px-1 text-accent font-semibold">
                            <TicketCheck className='h-4 w-4' />
                            Your Bookings
                        </CardDescription>
                        <CardTitle className="text-2xl font-semibold tabular-nums sm:text-3xl">
                            <span className='flex items-center gap-1 tracking-wide'>
                                1,250 hrs
                                {/* <StarIcon className='h-6 w-6'></StarIcon> */}
                            </span>
                            <div className='text-sm flex gap-1 text-primary/80 tracking-wide font-semibold items-center'>
                                Available in 5 Fun Passes.
                            </div>
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="flex-1 py-20">
                        23
                    </CardContent>
                    <CardFooter className="flex flex-row-reverse justify-between items-end gap-1.5 text-sm mt-0">
                        <div className="text-muted-foreground -mt-10">
                            <CircleStar className='text-accent/50 group-hover:scale-105 group-hover:rotate-45 transition-all h-20 w-20' />
                        </div>
                        <Button className="bg-primary text-white hover:bg-primary/90 px-6 py-2 tracking-wide font-semibold mt-2">
                            Get Access Pass @ 700/-
                        </Button>
                    </CardFooter>
                </Card></button>)
    }



    const FunMeterBlock = () => {

        const chartData = [
            { browser: "safari", visitors: 200, fill: "var(--color-safari)" },
        ]
        const chartConfig = {
            visitors: {
                label: "Visitors",
            },
            safari: {
                label: "Safari",
                color: "var(--chart-2)",
            },
        }

        const navigate = useNavigate();

        return (
            <button className=' w-full  md:w-96 '>
                <Card className="flex flex-col w-full transition-all relative group">
                    <ExternalLink className='absolute top-5 right-5 group-hover:text-primary'></ExternalLink>

                    <CardHeader className="items-start pb-0 space-y-0">
                        <CardTitle className="tracking-wide transition-all flex items-center gap-1 group-hover:border-accent group-hover:border-l-4 group-hover:px-1 text-accent font-semibold">
                            <CircleGauge className='h-4 w-4' />
                            <span className='text-sm'>Fun Meter</span>
                        </CardTitle>
                        <CardDescription className="text-primary/80 font-semibold tracking-wide m-0">
                            Hours of fun at ToonTopia.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="flex-1 pb-0">
                        <ChartContainer
                            config={chartConfig}
                            className="mx-auto aspect-square max-h-[250px]"
                        >
                            <RadialBarChart
                                data={chartData}
                                startAngle={0}
                                endAngle={250}
                                innerRadius={80}
                                outerRadius={110}
                            >
                                <PolarGrid
                                    gridType="circle"
                                    radialLines={false}
                                    stroke="none"
                                    className="first:fill-muted last:fill-background"
                                    polarRadius={[86, 74]}
                                />
                                <RadialBar dataKey="visitors" background cornerRadius={10} />
                                <PolarRadiusAxis tick={false} tickLine={false} axisLine={false}>
                                    <Label
                                        content={({ viewBox }) => {
                                            if (viewBox && "cx" in viewBox && "cy" in viewBox) {
                                                return (
                                                    <text
                                                        x={viewBox.cx}
                                                        y={viewBox.cy}
                                                        textAnchor="middle"
                                                        dominantBaseline="middle"
                                                    >
                                                        <tspan
                                                            x={viewBox.cx}
                                                            y={viewBox.cy}
                                                            className="fill-foreground text-4xl font-bold"
                                                        >
                                                            {chartData[0].visitors.toLocaleString()}+
                                                        </tspan>
                                                        <tspan
                                                            x={viewBox.cx}
                                                            y={(viewBox.cy || 0) + 24}
                                                            className="fill-muted-foreground"
                                                        >
                                                            Hours
                                                        </tspan>
                                                    </text>
                                                )
                                            }
                                        }}
                                    />
                                </PolarRadiusAxis>
                            </RadialBarChart>
                        </ChartContainer>
                    </CardContent>
                    <CardFooter className="flex-col gap-2 text-sm">
                        <div className="flex items-center gap-2 leading-none font-medium">
                            Trending up by 5.2% this month <TrendingUp className="h-4 w-4" />
                        </div>
                        <div className="text-muted-foreground leading-none">
                            Showing total visitors for the last 6 months
                        </div>
                    </CardFooter>
                </Card>
            </button>
        )
    }

    const GetMembershipBlock = ({ }) => {
        return (
            <button className="w-full lg:w-2/5">
                <Card className="w-full text-left border-0 group hover:shadow-lg m-0 transition-all relative">
                    <ExternalLink className='absolute top-5 right-5 group-hover:text-primary'></ExternalLink>
                    <CardHeader className=''>
                        <CardDescription className="tracking-wide transition-all flex items-center gap-1 group-hover:border-accent group-hover:border-l-4 group-hover:px-1 text-accent font-semibold">
                            <TicketCheck className='h-4 w-4' />
                            Fun Passes
                        </CardDescription>
                        <CardTitle className="text-2xl font-semibold tabular-nums sm:text-3xl">
                            <span className='flex items-center gap-1 tracking-wide'>
                                1,250 hrs
                                {/* <StarIcon className='h-6 w-6'></StarIcon> */}
                            </span>
                            <div className='text-sm flex gap-1 text-primary/80 tracking-wide font-semibold items-center'>
                                {/* <AlarmClockCheck className='h-4 w-4' /> 5 Hours
                                &middot;
                                <CalendarRange className='h-4 w-4' /> 11 Months */}
                                Available in 5 Fun Passes.
                            </div>
                        </CardTitle>
                    </CardHeader>
                    <CardFooter className="flex flex-row-reverse justify-between items-end gap-1.5 text-sm mt-0">
                        <div className="text-muted-foreground -mt-10">
                            <CircleStar className='text-accent/50 group-hover:scale-105 group-hover:rotate-45 transition-all h-20 w-20' />
                        </div>
                        <Button className="bg-primary text-white hover:bg-primary/90 px-6 py-2 tracking-wide font-semibold mt-2">
                            Get Access Pass @ 700/-
                        </Button>
                    </CardFooter>
                </Card></button>)
    }

    const RewardsBlock = ({ }) => {
        return (<button className="w-full md:w-2/4 lg:w-2/5">
            <Card className="w-full text-left border-0 group hover:shadow-lg transition-all relative m-0">
                <ExternalLink className='absolute top-5 right-5 group-hover:text-primary'></ExternalLink>
                <CardHeader>
                    <CardDescription className="tracking-wide transition-all flex items-center gap-1 group-hover:border-accent group-hover:border-l-4 group-hover:px-1 text-accent font-semibold">
                        <StarIcon className='h-4 w-4'></StarIcon>
                        Rewards
                    </CardDescription>
                    <CardTitle className="text-2xl font-semibold tabular-nums sm:text-3xl">
                        <span className='flex items-center gap-1'>
                            1,250
                            {/* <StarIcon className='h-6 w-6'></StarIcon> */}
                        </span>
                        <div className='text-sm flex gap-1 text-primary/80'>
                            <TrendingUp />
                            +12.5%
                        </div>
                    </CardTitle>
                </CardHeader>
                <CardFooter className="flex-col items-start gap-1.5 text-sm">
                    <div className="line-clamp-1 flex gap-2 font-medium">
                        Trending up this month <TrendingUp className="size-4" />
                    </div>
                    <div className="text-muted-foreground">
                        Visitors for the last 6 months
                    </div>
                </CardFooter>
            </Card></button>)
    }

    // Function to render the appropriate membership card based on userTierData
    const renderMembershipSection = () => {

        let gradientColors = '';
        let IconComponent = null;

        switch (userTierData.tier) {
            case 'S2':
                gradientColors = 'bg-gradient-to-br from-yellow-500 to-orange-600'; // Gold/Orange gradient
                IconComponent = Award; // Gold badge icon
                break;
            case 'S3':
                gradientColors = 'bg-gradient-to-br from-blue-500 to-indigo-600'; // Blue gradient
                IconComponent = Star; // Blue badge icon
                break;
            case 'S4':
                gradientColors = 'bg-gradient-to-br from-purple-500 to-pink-600'; // Purple/Pink gradient
                IconComponent = Crown; // Crown/Platinum badge icon
                break;
            default:
                gradientColors = 'bg-gradient-to-br from-gray-500 to-gray-600'; // Default fallback
                IconComponent = Star;
        }

        return (
            <>
                <Navbar></Navbar>
                <MainContainer>
                    <div className='flex-wrap flex gap-4 md:gap-8'>
                        <FunMeterBlock></FunMeterBlock>
                        <BookingBlock></BookingBlock>
                        <br></br>
                        <RewardsBlock></RewardsBlock>
                        <GetMembershipBlock></GetMembershipBlock>

                    </div>
                </MainContainer>
            </>

        );
    };

    if (isAuthenticated) {
        console.log("[Home] logged in as ", user)
        return renderMembershipSection();
    }
    else
        return <SplashScreen></SplashScreen>
};
export default HomeScreen;